import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, ShoppingCart, Wallet, PackagePlus, Truck, UserPlus, Clock, XCircle, Store, Contact } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function FABQuickActions() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { tenantId } = useTenant();

  const { data: openSession } = useQuery({
    queryKey: ["fab-open-session", tenantId],
    queryFn: async () => {
      if (!tenantId) return null as any;
      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select("id, status")
        .eq("tenant_id", tenantId)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      return (data && data.length > 0) ? data[0] : null;
    },
    enabled: !!tenantId,
    staleTime: 15000,
  });

  const actions = useMemo(() => [
    openSession
      ? {
          label: "Fechar Caixa",
          icon: <XCircle className="h-4 w-4" />,
          onClick: () => navigate("/caixa?closeCash=1"),
          color: "bg-primary hover:bg-primary/90 text-primary-foreground",
        }
      : {
          label: "Abrir Caixa",
          icon: <Clock className="h-4 w-4" />,
          onClick: () => navigate("/caixa?openCash=1"),
          color: "bg-primary hover:bg-primary/90 text-primary-foreground",
        },
    {
      label: "PDV - Tela de Vendas",
      icon: <Store className="h-4 w-4" />,
      onClick: () => navigate("/pdv"),
      color: "bg-violet-600 hover:bg-violet-700 text-white",
    },
    {
      label: "Cadastrar nova venda",
      icon: <ShoppingCart className="h-4 w-4" />, 
      onClick: () => navigate("/caixa?newSale=1"),
      color: "bg-emerald-600 hover:bg-emerald-700 text-white",
    },
    {
      label: "Cadastrar nova despesa",
      icon: <Wallet className="h-4 w-4" />, 
      onClick: () => navigate("/caixa?newExpense=1"),
      color: "bg-red-600 hover:bg-red-700 text-white",
    },
    {
      label: "Cadastrar novo cliente",
      icon: <Contact className="h-4 w-4" />,
      onClick: () => navigate("/clientes?newClient=1"),
      color: "bg-primary/90 hover:bg-primary text-primary-foreground",
    },
    {
      label: "Cadastrar novo produto",
      icon: <PackagePlus className="h-4 w-4" />, 
      onClick: () => navigate("/produtos?newProduct=1"),
      color: "bg-primary/90 hover:bg-primary text-primary-foreground",
    },
    {
      label: "Cadastrar novo fornecedor",
      icon: <Truck className="h-4 w-4" />, 
      onClick: () => navigate("/fornecedores?newSupplier=1"),
      color: "bg-primary/90 hover:bg-primary text-primary-foreground",
    },
    {
      label: "Cadastrar novo usuário",
      icon: <UserPlus className="h-4 w-4" />, 
      onClick: () => navigate("/usuarios?newUser=1"),
      color: "bg-primary/90 hover:bg-primary text-primary-foreground",
    },
  ], [openSession, navigate]);

  return (
    <div className="fixed bottom-6 right-6 z-50 select-none">
      <div className="relative" style={{ width: 48, height: 48 }}>
        {/* Actions: posicionadas absolutamente para não deslocar o FAB */}
        <div
          className={`absolute right-0 bottom-14 flex flex-col items-end gap-2 ${open ? "pointer-events-auto" : "pointer-events-none"}`}
          aria-hidden={!open}
          tabIndex={open ? 0 : -1}
        >
          {actions.map((action, i) => {
            const colorClass = (action as any).color as string;
            return (
              <Button
                key={action.label}
                type="button"
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
                className={`shadow-md px-3 h-9 rounded-full ${colorClass} transition-all duration-300 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
                style={{ transitionDelay: open ? `${i * 50}ms` : "0ms" }}
              >
                <span className="mr-2 opacity-90">{action.icon}</span>
                <span className="text-sm whitespace-nowrap">{action.label}</span>
              </Button>
            );
          })}
        </div>

        {/* FAB */}
        <Button
          type="button"
          size="icon"
          onClick={() => setOpen((v) => !v)}
          className="absolute right-0 bottom-0 h-12 w-12 rounded-full shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground"
          aria-label={open ? "Fechar ações rápidas" : "Abrir ações rápidas"}
        >
          <Plus className={`h-6 w-6 transition-transform duration-300 ${open ? "rotate-45" : "rotate-0"}`} />
        </Button>
      </div>
    </div>
  );
}
